import React, { useRef, useState } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { Spacer } from "../../spacer/spacer";
import upperFirst from "lodash.upperfirst";
import { EditTextInput } from "../../index";
import toLower from "lodash.tolower";
import { PaymentSourceType } from "../../../enums";
import { shouldDisablePayment } from "../../../hooks/usePaymentMethodInformation";
import {
  theme as componentTheme,
  Text as TextComponent,
  typography,
  Icon,
  Icons,
  ErrorWarningBottomSheet,
  theme,
} from "ui-components";
import { fontWeights } from "ui-components/dist/theme/typography";
import { translate } from "../../../i18n";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";
import { dbToAccountTypeFieldValueMapping } from "../../../screens/new/utils/helper";
import { DomusoApiService } from "../../../api/DomusoApiService";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { domusoResponsiveStyleHelper } from "../../../screens/new/utils/domuso-responsive";

interface SinglePaymentSource {
  paymentType?: PaymentSourceType;
  allow_cof?: boolean;
  id?: number | string;
  street_address?: string | null;
  address_line2?: string | null;
  city?: string | null;
  last_four_digits?: string;
  state?: string | null;
  cardholder?: string;
  zip?: number;
  card_type?: "VISA" | "MASTERCARD" | "MASTER CARD" | "DISCOVER";
}
export interface PaymentMethodProps extends SinglePaymentSource {
  onPress?: any;
  navigation?: any;
  allowedPayments?: "ANY" | "DO_NOT_ACCEPT_PAYMENT" | "CASH_EQUIVALENT";
  index?: number;
  bank_account_type?: string;
  shouldDisableCC?: boolean;
  id?: string | number;
  certifiedDateRange?: any;
  emitSelected?: any;
  selected?: any;
  useRadioButton?: boolean;
  withinCertifiedDateRange?: any;
  checkedStatus?: "checked" | "unchecked";
  setSelectedWrapped?: any;
  fullName?: string;
  active?: boolean;
  allowCC?: boolean;
  allowACH?: boolean;
  disabledMethod?: boolean;
  nickname?: string;
  updateNickname?: any;
  updateStatus?: any;
  deleteOption?: boolean;
  updated?: void;
}

interface ErrorMsgObjectProps {
  title?: string;
  body?: string;
}

export const PaymentMethod = (paymentMethodProps: PaymentMethodProps) => {
  let {
    active,
    last_four_digits,
    bank_account_type,
    allowedPayments,
    useRadioButton = true,
    withinCertifiedDateRange,
    checkedStatus,
    setSelectedWrapped,
    fullName,
    card_type,
    shouldDisableCC = false,
    paymentType,
    allowCC = true,
    allowACH = true,
    id,
    nickname,
    updateNickname,
    updateStatus,
    deleteOption,
    updated,
    ...otherProps
  } = paymentMethodProps;
  const [isLoading, setLoading] = useState(false);
  const [isRemoveLoading, setRemoveLoading] = useState(false);
  const [errorMsgObject, setErrorMsgObject] = useState<ErrorMsgObjectProps>({});
  const refDeletePaymentSourceAlert = useRef<any>();
  const refPopupErrorDeletePaymentSource = useRef<any>();
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const service = new DomusoApiService(cognitoUser, dispatch);
  const isCertifiedMethod = false;
  let isBankAccount = paymentType == PaymentSourceType.BANK_ACCOUNT;

  let disabledMethod =
    shouldDisablePayment({ isCertifiedMethod, allowedPayments }) ||
    withinCertifiedDateRange ||
    !active;
  if (!isBankAccount && (shouldDisableCC || !allowCC)) {
    disabledMethod = true;
    useRadioButton = false;
  }
  if (isBankAccount && !allowACH) {
    disabledMethod = true;
    useRadioButton = false;
  }
  let [nickNameState, setNickName] = useState(nickname);

  let eyeIconColor = disabledMethod ? componentTheme.colors.grey : componentTheme.colors.primary;

  let styles = getStyles(disabledMethod);

  const showUnexpectedError = (error?: any) => {
    setLoading(false);
    if (
      error &&
      (error == "networkError" ||
        error?.message == "Failed to fetch" ||
        error?.message == "Network request failed")
    ) {
      setErrorMsgObject({
        title: translate("myAccount.connectionErrorTitle") as string,
        body: translate("myAccount.connectionErrorMessage") as string,
      });
      return;
    } else {
      setErrorMsgObject({
        title: translate("myAccount.error") as string,
        body: translate("myAccount.unexpectedError") as string,
      });
      return;
    }
  };

  const onDeleteConfirmation = async (paymentSourceId: number | string | any) => {
    setRemoveLoading(true);
    try {
      // console.log(`Preparing to delete payment source id ${paymentSourceId}`);
      let response = await service.deletePaymentSource(paymentSourceId);
      if (!paymentSourceId || !response) {
        setRemoveLoading(false);
        showUnexpectedError(new Error("Payment Source ID does not exist"));
        refPopupErrorDeletePaymentSource?.current?.open();
        return;
      }
      updated();
      refDeletePaymentSourceAlert?.current?.close();
    } catch (error) {
      // console.log(error);
      setRemoveLoading(false);
      showUnexpectedError(error);
      refPopupErrorDeletePaymentSource?.current?.open();
    }
  };

  return (
    <>
      <View style={styles.cardContainer}>
        <View style={{ flexDirection: "row", justifyContent: "space-between" }}>
          <View style={{}}>
            <TextComponent
              style={[typography.body, styles.label]}
              {...(isBankAccount
                ? automationTestingLocators.walletPaymentMethodsPaymentMethod.accountNumberTitle
                : automationTestingLocators.walletPaymentMethodsPaymentMethod.cardNumberTitle)}>
              {isBankAccount
                ? translate("walletPaymentMethod.bank.accountNumber")
                : translate("walletPaymentMethod.card.cardNumber")}
            </TextComponent>
            <View style={{ flexDirection: "row" }}>
              <TextComponent
                style={[typography.title2, styles.title]}
                {...(isBankAccount
                  ? automationTestingLocators.walletPaymentMethodsPaymentMethod.accountNumber
                  : automationTestingLocators.walletPaymentMethodsPaymentMethod.cardNumber)}>
                {isBankAccount
                  ? `xxxx ${last_four_digits}`
                  : `${upperFirst(toLower(card_type))} ...xxxx ${last_four_digits}`}
              </TextComponent>
            </View>
            <TextComponent
              style={[typography.body, active ? styles.active : styles.inactive]}
              {...automationTestingLocators.walletPaymentMethodsPaymentMethod.status}>
              {active
                ? translate("walletPaymentMethod.active")
                : translate("walletPaymentMethod.inactive")}
            </TextComponent>
          </View>
          {isBankAccount ? (
            <View style={{}}>
              <TextComponent
                style={[
                  typography.body,
                  styles.label,
                  {
                    textAlign: "right",
                  },
                ]}
                {...automationTestingLocators.walletPaymentMethodsPaymentMethod.accountTypeTitle}>
                {translate("walletPaymentMethod.bank.accountType")}
              </TextComponent>
              <TextComponent
                style={[
                  typography.title2,
                  styles.title,
                  {
                    textAlign: "right",
                  },
                ]}
                {...automationTestingLocators.walletPaymentMethodsPaymentMethod.accountType}>
                {dbToAccountTypeFieldValueMapping(bank_account_type)}
              </TextComponent>
            </View>
          ) : (
            <View
              style={{}}
              {...automationTestingLocators.walletPaymentMethodsPaymentMethod.cardTypeIconBox}>
              <Icon
                name={
                  card_type == "VISA"
                    ? Icons["visa-card"]
                    : card_type == "MASTERCARD" || card_type == "MASTER CARD"
                    ? Icons["master-card"]
                    : Icons["discover-card"]
                }
                size={componentTheme.iconSize.medium}
                color={eyeIconColor}
              />
            </View>
          )}
        </View>
        <Spacer size="larger"></Spacer>

        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
          <View style={{ maxWidth: "80%" }}>
            <EditTextInput
              maxTextLength={27}
              value={nickNameState}
              placeholder={translate("walletPaymentMethod.nicknameTitle") as string}
              onChangeText={(text: string) => setNickName(text)}
              onBlur={() => {
                if (nickname !== nickNameState) {
                  updateNickname(id, nickNameState);
                }
              }}
              disabled={disabledMethod}
              {...automationTestingLocators.walletPaymentMethodsPaymentMethod.nicknameInput}
            />
          </View>
          {deleteOption ? (
            <View style={{ maxWidth: "80%" }}>
              <TouchableOpacity
                onPress={() => refDeletePaymentSourceAlert?.current?.open()}
                style={styles.deleteIconContainer}>
                <Icon
                  name={"delete"}
                  size={componentTheme.iconSize.medium}
                  color={componentTheme.colors.primary}
                />
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      </View>
      <ErrorWarningBottomSheet
        height={300}
        alertType="warning"
        ref={refDeletePaymentSourceAlert}
        title={translate("myPaymentMethods.deletePaymentSourceAlertTitle") as string}
        body={!active ? "" : (translate("myPaymentMethods.deletePaymentSourceAlertBody") as string)}
        leftButtonType={"secondary"}
        rightButtonType={"primary"}
        leftButtonText={
          translate("myPaymentMethods.deletePaymentSourceAlertDeleteButton") as string
        }
        rightButtonText={
          translate("myPaymentMethods.deletePaymentSourceAlertCancelButton") as string
        }
        leftButtonTextStyle={{ color: theme.colors.alert }}
        leftButtonStyle={styles.leftButtonAlert}
        rightButtonStyle={styles.rightButtonAlert}
        rightButtonTextStyle={{ fontFamily: "Lato_700Bold" }}
        buttonsContainer={{ flexDirection: "column", marginTop: 30 }}
        containerStyleModal={{ maxWidth: 400 }}
        onPressRightButton={() => refDeletePaymentSourceAlert?.current?.close()}
        onPressLeftButton={() => {
          onDeleteConfirmation(id);
        }}
        bodyTextStyle={styles.alertBodyText}
        leftButtonLoading={isRemoveLoading}
        rightButtonLoading={isRemoveLoading}
        leftButtonDisabled={isRemoveLoading}
        rightButtonDisabled={isRemoveLoading}>
        <ErrorWarningBottomSheet
          ref={refPopupErrorDeletePaymentSource}
          title={errorMsgObject?.title}
          body={errorMsgObject?.body}
          height={150}
          alertType={"error"}
          showButtons={false}
        />
      </ErrorWarningBottomSheet>
    </>
  );
};

const getStyles = (disabledMethod: boolean) => {
  const theme = componentTheme;
  return StyleSheet.create({
    cardContainer: {
      padding: 12,
      borderWidth: 1,
      borderColor: theme.colors.mediumGrey,
      borderRadius: 10,
      ...(disabledMethod ? { backgroundColor: theme.colors.lightGrey } : {}),
    },
    label: {
      fontWeight: fontWeights.Regular,
      color: theme.colors.grey,
      ...(disabledMethod ? { color: theme.colors.darkerGrey } : {}),
    },
    deleteAccountButton: {
      padding: 15,
      outlineStyle: "none",
    },
    title: {
      fontFamily: "Lato_700Bold",
      fontWeight: fontWeights.Bold,
      color: theme.colors.darkerGrey,
      ...(disabledMethod ? { color: theme.colors.darkerGrey } : {}),
    },
    eyeContainer: {
      marginLeft: 10,
      padding: 3,
      paddingBottom: 0,
    },
    leftButtonAlert: {
      borderColor: theme.colors.alert,
      ...domusoResponsiveStyleHelper({
        iosTablet: { flex: 0 },
        androidTablet: { flex: 0 },
      }),
    },
    rightButtonAlert: {
      ...domusoResponsiveStyleHelper({
        iosTablet: { flex: 0 },
        androidTablet: { flex: 0 },
      }),
    },
    alertBodyText: {
      textAlign: "center",
      color: theme.colors.text3,
    },
    deleteIconContainer: {
      borderWidth: 1,
      padding: 8,
      borderRadius: 100,
      borderColor: theme.colors.mediumGrey,
      backgroundColor: "#FCFCFC",
    },
    active: {
      fontFamily: "Lato_700Bold",
      fontWeight: fontWeights.Bold,
      color: theme.colors.success,
      ...(disabledMethod ? { color: theme.colors.darkerGrey } : {}),
    },
    inactive: {
      fontFamily: "Lato_700Bold",
      fontWeight: fontWeights.Bold,
      lineHeight: 18,
      color: theme.colors.error,
      ...(disabledMethod ? { color: theme.colors.darkerGrey } : {}),
    },

    cardContainerWeb: {
      padding: 12,
      borderTopWidth: 1,
      borderBottomWidth: 1,
      borderColor: theme.colors.mediumGrey,
      justifyContent: "center",
      alignContent: "center",
    },
  });
};
